/**
 * Yjs Update Batcher
 *
 * Batches rapid Yjs updates with 50ms debounce and optional compression.
 * Implements the batching logic for SPEC-008 client-side sync.
 *
 * Features:
 * - 50ms debounce window to batch rapid edits
 * - Automatic compression for updates >1KB (using pako deflate)
 * - Merges multiple Yjs updates into single update
 * - Thread-safe flush behavior
 *
 * @module features/collab/yjs/updateBatcher
 * @see SPEC-008 Requirements 2.7, 2.8
 */

import * as Y from 'yjs';
import pako from 'pako';
import { fromUint8Array, toUint8Array } from 'js-base64';
import {
  type YjsUpdateMessage,
  YJS_BATCH_INTERVAL_MS,
  YJS_COMPRESSION_THRESHOLD,
} from '@linkode/shared';

// ============================================================================
// Types
// ============================================================================

/**
 * Batched update result from flush operation
 */
export interface BatchedUpdate {
  /** Binary data (may be compressed) */
  data: Uint8Array;
  /** Whether data is pako deflate compressed */
  compressed: boolean;
  /** Original uncompressed size in bytes */
  originalSize: number;
}

/**
 * Configuration for UpdateBatcher
 */
export interface UpdateBatcherConfig {
  /** Debounce interval in milliseconds (default: 50ms) */
  intervalMs?: number;
  /** Compression threshold in bytes (default: 1024) */
  compressionThreshold?: number;
  /** Client ID for update messages */
  clientId: string;
  /** Callback when batch is flushed */
  onFlush?: (update: BatchedUpdate) => void;
  /** Callback when formatted message is ready for transport */
  onMessage?: (message: YjsUpdateMessage) => void;
}

// ============================================================================
// UpdateBatcher Class
// ============================================================================

/**
 * Batches Yjs updates with debouncing and optional compression.
 *
 * @example
 * ```typescript
 * const batcher = new UpdateBatcher({
 *   clientId: 'client-123',
 *   onMessage: (msg) => {
 *     // Send msg via Realtime transport
 *     console.log(msg.type, msg.compressed, msg.originalSize);
 *   },
 * });
 *
 * // Add updates from Y.Doc observer
 * yDoc.on('update', (update) => batcher.addUpdate(update));
 *
 * // Cleanup on unmount
 * batcher.destroy();
 * ```
 */
export class UpdateBatcher {
  private readonly intervalMs: number;
  private readonly compressionThreshold: number;
  private readonly clientId: string;
  private readonly onFlush?: (update: BatchedUpdate) => void;
  private readonly onMessage?: (message: YjsUpdateMessage) => void;

  private pendingUpdates: Uint8Array[] = [];
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(config: UpdateBatcherConfig) {
    this.intervalMs = config.intervalMs ?? YJS_BATCH_INTERVAL_MS;
    this.compressionThreshold = config.compressionThreshold ?? YJS_COMPRESSION_THRESHOLD;
    this.clientId = config.clientId;
    this.onFlush = config.onFlush;
    this.onMessage = config.onMessage;
  }

  /**
   * Add a Yjs update to the pending batch.
   * Schedules a flush after the debounce interval.
   *
   * @param update - Yjs V2 update binary
   */
  addUpdate(update: Uint8Array): void {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console -- Debug logging
      console.log('[UpdateBatcher] addUpdate called', {
        pendingCount: this.pendingUpdates.length,
        updateSize: update.byteLength,
        hasTimer: this.timer !== null,
      });
    }
    this.pendingUpdates.push(update);
    this.scheduleFlush();
  }

  /**
   * Immediately flush all pending updates.
   * Useful for ensuring updates are sent before page unload.
   */
  flush(): void {
    this.cancelScheduledFlush();
    this.doFlush();
  }

  /**
   * Get current pending update count.
   * Useful for metrics and testing.
   */
  getPendingCount(): number {
    return this.pendingUpdates.length;
  }

  /**
   * Check if there are pending updates waiting to be flushed.
   */
  hasPendingUpdates(): boolean {
    return this.pendingUpdates.length > 0;
  }

  /**
   * Destroy the batcher, canceling any scheduled flush.
   * Call this on component unmount.
   */
  destroy(): void {
    this.cancelScheduledFlush();
    this.pendingUpdates = [];
  }

  private scheduleFlush(): void {
    if (this.timer !== null) return;
    this.timer = setTimeout(() => {
      this.timer = null;
      this.doFlush();
    }, this.intervalMs);
  }

  private cancelScheduledFlush(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private doFlush(): void {
    if (this.pendingUpdates.length === 0) return;

    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console -- Debug logging
      console.log('[UpdateBatcher] doFlush called', {
        pendingCount: this.pendingUpdates.length,
        hasOnMessage: !!this.onMessage,
      });
    }

    // Merge all pending updates using Yjs V2 API
    const merged = Y.mergeUpdates(this.pendingUpdates);
    this.pendingUpdates = [];

    // Compress if exceeds threshold
    const originalSize = merged.byteLength;
    const shouldCompress = originalSize > this.compressionThreshold;
    const data = shouldCompress ? pako.deflate(merged) : merged;

    const batchedUpdate: BatchedUpdate = {
      data,
      compressed: shouldCompress,
      originalSize,
    };

    // Invoke callbacks
    this.onFlush?.(batchedUpdate);

    // Build transport message if onMessage handler exists
    if (this.onMessage) {
      const message: YjsUpdateMessage = {
        type: 'yjs-update',
        data: fromUint8Array(data),
        compressed: shouldCompress,
        originalSize,
        clientId: this.clientId,
        timestamp: Date.now(),
      };

      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console -- Debug logging
        console.log('[UpdateBatcher] calling onMessage', {
          originalSize: message.originalSize,
          compressed: message.compressed,
        });
      }

      this.onMessage(message);
    }
  }
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Decode a YjsUpdateMessage back to Uint8Array.
 * Handles decompression if the message was compressed.
 *
 * @param message - The received YjsUpdateMessage
 * @returns Decoded Yjs update binary
 *
 * @example
 * ```typescript
 * const update = decodeUpdateMessage(message);
 * Y.applyUpdate(yDoc, update);
 * ```
 */
export function decodeUpdateMessage(message: YjsUpdateMessage): Uint8Array {
  const raw = toUint8Array(message.data);

  if (message.compressed) {
    return pako.inflate(raw);
  }
  return raw;
}

/**
 * Encode a Yjs update to YjsUpdateMessage with optional compression.
 * Standalone version of batcher's encode logic for one-off sends.
 *
 * @param update - Yjs V2 update binary
 * @param clientId - Client identifier
 * @param compressionThreshold - Threshold in bytes (default: 1024)
 * @returns Formatted YjsUpdateMessage
 */
export function encodeUpdateMessage(
  update: Uint8Array,
  clientId: string,
  compressionThreshold: number = YJS_COMPRESSION_THRESHOLD
): YjsUpdateMessage {
  const originalSize = update.byteLength;
  const shouldCompress = originalSize > compressionThreshold;
  const data = shouldCompress ? pako.deflate(update) : update;

  return {
    type: 'yjs-update',
    data: fromUint8Array(data),
    compressed: shouldCompress,
    originalSize,
    clientId,
    timestamp: Date.now(),
  };
}
